import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { customerApi } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Tag, Copy, Sparkles, ArrowRight, Lock, Gift } from "lucide-react";

const TYPE_LABEL = {
  percentage: "Discount",
  sitewide: "Sitewide Sale",
  category: "Category Offer",
  announcement: "Announcement",
};

export default function Offers() {
  const { customer } = useCustomerAuth();
  const [offers, setOffers] = useState([]);
  const [personalCodes, setPersonalCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOffers = useCallback(async () => {
    try {
      const { data } = await customerApi().get("/offers");
      setOffers(data.offers || []);
      setPersonalCodes(data.personal_codes || []);
    } catch {
      /* handled by gate */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (customer === null) return;
    if (customer) loadOffers();
    else setLoading(false);
  }, [customer, loadOffers]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copied`);
    } catch {
      toast.error(`Copy manually: ${code}`);
    }
  };

  return (
    <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 py-14 min-h-[70vh]" data-testid="offers-page">
      <Seo
        title="Exclusive Offers & Discount Codes"
        description="Sign in to your Zebaa account to unlock exclusive member offers and discount codes on bridal sets, necklace sets, bangles and earrings."
        path="/offers"
        noindex
      />
      <Reveal className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Members Only</p>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3 text-charcoal">Exclusive Offers</h1>
        <p className="text-gray-500 mt-3">Deals & discount codes reserved for Zebaa account holders.</p>
        <div className="w-16 h-px bg-gold mx-auto mt-6" />
      </Reveal>

      {loading || customer === null ? (
        <p className="text-center text-gray-400 py-20">Loading...</p>
      ) : !customer ? (
        <div className="max-w-md mx-auto text-center py-6" data-testid="offers-gate">
          <div className="w-16 h-16 rounded-full bg-pink/10 flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-pink" />
          </div>
          <h2 className="font-serif text-2xl text-charcoal mt-6">Create an account to unlock offers</h2>
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">
            Our exclusive offers are reserved for Zebaa members. Create a free account or sign in to view them.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register?redirect=/offers" data-testid="offers-register-btn"
              className="bg-pink text-white px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">
              Create Account
            </Link>
            <Link to="/login?redirect=/offers" data-testid="offers-login-btn"
              className="border border-charcoal text-charcoal px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-charcoal hover:text-white transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      ) : (
        <>
          {personalCodes.length > 0 && (
            <Reveal className="max-w-5xl mx-auto mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-metallic-gold" />
                <h2 className="font-serif text-2xl text-charcoal">Your Personal Offers</h2>
                <span className="ml-1 text-xs bg-pink text-white rounded-full px-2.5 py-0.5" data-testid="personal-offers-count">{personalCodes.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {personalCodes.map((p) => (
                  <div key={p.code} data-testid={`personal-code-${p.code}`}
                    className="relative overflow-hidden rounded-lg p-6 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                    style={{ background: "linear-gradient(135deg, #2a1622 0%, #4a2036 55%, #6d2a4c 100%)" }}>
                    <div className="flex items-center gap-4">
                      <Gift className="w-8 h-8 text-gold-bright shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/60">
                          {p.kind === "welcome" ? "First Order Reward" : "Returning Customer Reward"}
                        </p>
                        <p className="font-sans font-bold text-3xl mt-1" style={{ background: "linear-gradient(180deg,#f8ecbc 0%,#edd48c 40%,#d9b45f 60%,#f4e2a4 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                          {p.percent}% OFF
                        </p>
                        <p className="font-mono text-sm tracking-[0.15em] text-white mt-1">{p.code}</p>
                        <p className="text-[11px] text-white/50 mt-1">
                          {p.first_order_only ? "Single use · first order only · your account only" : "Single use · your account only"}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => copyCode(p.code)} data-testid={`personal-copy-${p.code}`}
                      className="bg-white/95 text-charcoal px-5 py-2.5 rounded-full flex items-center gap-2 text-xs uppercase tracking-wide hover:bg-white transition-colors shrink-0">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {offers.length === 0 ? (
            personalCodes.length === 0 && (
            <div className="text-center py-16" data-testid="offers-empty">
              <Tag className="w-14 h-14 text-gray-200 mx-auto" />
              <p className="text-gray-500 mt-5">No live offers right now - check back soon for exclusive deals.</p>
              <Link to="/shop/all" className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Shop All</Link>
            </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {offers.map((o, i) => (
                <Reveal key={o.id} delay={(i % 2) * 0.08}>
                  <div className="relative bg-white border border-gray-100 overflow-hidden h-full flex flex-col" data-testid={`offer-card-${o.id}`}>
                    <div className="bg-pink text-white px-6 py-5 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.2em] text-white/85">{TYPE_LABEL[o.type] || "Offer"}</span>
                      {o.value ? <span className="font-sans font-semibold text-3xl text-metallic-gold-on-dark">{o.value}% OFF</span> : <Sparkles className="w-6 h-6 text-gold-bright" />}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-serif text-2xl text-charcoal">{o.title}</h3>
                      {o.description && <p className="text-sm text-gray-500 mt-2 leading-relaxed flex-1">{o.description}</p>}
                      {o.category && <p className="text-xs uppercase tracking-wide text-gray-400 mt-3">On {o.category.replace("-", " ")}</p>}
                      {o.code && (
                        <div className="mt-5 flex items-stretch">
                          <div className="flex-1 border-2 border-dashed border-pink text-pink font-semibold tracking-[0.2em] flex items-center justify-center py-2.5">{o.code}</div>
                          <button onClick={() => copyCode(o.code)} data-testid={`offer-copy-${o.id}`} className="bg-pink text-white px-4 flex items-center gap-1.5 text-xs uppercase hover:bg-pink-hover transition-colors">
                            <Copy className="w-4 h-4" /> Copy
                          </button>
                        </div>
                      )}
                      <Link to={o.category ? `/shop/${o.category}` : "/shop/all"} className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-charcoal hover:text-pink transition-colors">
                        Shop Now <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
